import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { FiCalendar } from '@react-icons/all-files/fi/FiCalendar'
import { FiHome } from '@react-icons/all-files/fi/FiHome'
import { FiTruck } from '@react-icons/all-files/fi/FiTruck'
import ProductionPopup from '../../DeferredProductionPopup'
import QuickRental from '../../QuickRental'
import { helper } from '../../../utilities/helper'
import { productCanBePickedUp } from '../../../utilities/orderProductHydration'
import UiBoundary from '../foundations/UiBoundary'
import Media from '../primitives/Media'
import styles from './RentalStartPopup.module.scss'
import { requiresMiamiFulfillment } from '../../../utilities/fulfillmentMarket'

const RentalStartPopup = ({
  product,
  orlando = false,
  onSelect,
  resolveProduct,
  selectionOnly = false,
  pickupAvailableOverride,
  rentalData,
  deliveryItems,
  builder,
  builderIndex,
  appendToExistingItems = false
}) => {
  const [resolvedProduct, setResolvedProduct] = useState(resolveProduct ? null : product)
  const [loadingProduct, setLoadingProduct] = useState(Boolean(resolveProduct))
  const [loadError, setLoadError] = useState('')
  const productResolutionRef = useRef({ productKey: null, promise: null })
  const activeProduct = resolvedProduct || product
  const pickupAvailable = typeof pickupAvailableOverride === 'boolean'
    ? pickupAvailableOverride
    : productCanBePickedUp(activeProduct, orlando)
  const initialPickupAvailable = typeof pickupAvailableOverride === 'boolean'
    ? pickupAvailableOverride
    : productCanBePickedUp(product, orlando)
  const [fulfillment, setFulfillment] = useState(selectionOnly || !initialPickupAvailable ? null : 'pickup')
  const automaticDeliveryRef = useRef(false)
  const orderItem = useMemo(() => ({
    product: activeProduct,
    units: 1,
    category: activeProduct?.categories?.[0]?.split('/')?.[1]
  }), [activeProduct])
  const pickupReason = activeProduct?.requires_tech
    ? 'This item requires professional delivery and installation.'
    : !activeProduct?.allow_pickup
      ? 'Warehouse pickup is not available for this item.'
      : orlando && requiresMiamiFulfillment(activeProduct)
        ? 'This item is available from Miami. Delivery will use Miami fulfillment and may include travel.'
        : ''

  useEffect(() => {
    if (!resolveProduct) return
    let active = true
    const productKey = String(product?._id || product?.name || '')
    if (
      productResolutionRef.current.productKey !== productKey
      || !productResolutionRef.current.promise
    ) {
      productResolutionRef.current = {
        productKey,
        promise: Promise.resolve().then(resolveProduct)
      }
    }

    setLoadingProduct(true)
    productResolutionRef.current.promise
      .then(nextProduct => {
        if (!active) return
        if (nextProduct) setResolvedProduct(nextProduct)
        else setLoadError('We could not verify this product. Please close this window and try again.')
      })
      .catch(() => {
        if (active) setLoadError('We could not verify this product. Please close this window and try again.')
      })
      .finally(() => {
        if (active) setLoadingProduct(false)
      })
    return () => { active = false }
  }, [product?._id, product?.name, resolveProduct])

  const selectFulfillment = useCallback(async nextFulfillment => {
    if (nextFulfillment === 'pickup' && !pickupAvailable) return false
    const accepted = await onSelect?.(nextFulfillment === 'delivery', activeProduct)
    if (accepted === false) return false
    setFulfillment(nextFulfillment)
    return true
  }, [activeProduct, onSelect, pickupAvailable])

  useEffect(() => {
    if (pickupAvailable || fulfillment === 'delivery' || !activeProduct || automaticDeliveryRef.current) return

    automaticDeliveryRef.current = true
    selectFulfillment('delivery').then(selected => {
      if (!selected) automaticDeliveryRef.current = false
    }).catch(() => {
      automaticDeliveryRef.current = false
    })
  }, [activeProduct, fulfillment, pickupAvailable, selectFulfillment])

  useEffect(() => {
    if (!fulfillment || typeof window === 'undefined' || !window.matchMedia('(max-width: 699px)').matches) return undefined

    const restoreDialogStart = () => {
      document.querySelector('.popup.rentalStartShell')?.scrollTo({ top: 0, behavior: 'auto' })
    }
    const frame = window.requestAnimationFrame(restoreDialogStart)
    const timer = window.setTimeout(restoreDialogStart, 120)

    return () => {
      window.cancelAnimationFrame(frame)
      window.clearTimeout(timer)
    }
  }, [fulfillment])

  return (
    <UiBoundary className={styles.root}>
      <h2 id='rental-start-title' className={styles.srOnly}>Rent {activeProduct?.name}</h2>
      <div className={styles.product}>
        <Media
          picture={activeProduct?.picture}
          alt={activeProduct?.name}
          sizes='72px'
          ratio='1 / 1'
          className={styles.productMedia}
        />
        <div>
          <span>Starting with</span>
          <strong>{activeProduct?.name}</strong>
        </div>
        <b className={styles.productPrice}>
          <strong>{helper.formatPrice(activeProduct?.price?.rental)}</strong>
          <span>/ 24h</span>
        </b>
      </div>

      <div className={`${styles.options} ${fulfillment ? styles.hasSelection : ''}`} role='group' aria-label='Choose pickup or delivery'>
        <button
          type='button'
          className={`${styles.option} ${fulfillment === 'pickup' ? styles.selected : ''}`}
          disabled={!pickupAvailable}
          aria-pressed={fulfillment === 'pickup'}
          onClick={() => selectFulfillment('pickup')}
        >
          <span className={styles.optionIcon}><FiHome aria-hidden='true' /></span>
          <span className={styles.optionCopy}>
            <strong>Warehouse pickup rental</strong>
            <span>Pick up and return the equipment at our warehouse.</span>
            <small>{pickupAvailable ? 'Pay the equipment rental price only' : pickupReason}</small>
          </span>
          <span className={styles.optionState}>{pickupAvailable ? (fulfillment === 'pickup' ? 'Selected' : 'Select') : 'Unavailable'}</span>
        </button>

        <button
          type='button'
          className={`${styles.option} ${fulfillment === 'delivery' ? styles.selected : ''}`}
          aria-pressed={fulfillment === 'delivery'}
          onClick={() => selectFulfillment('delivery')}
        >
          <span className={styles.optionIcon}><FiTruck aria-hidden='true' /></span>
          <span className={styles.optionCopy}>
            <strong>Delivery &amp; setup</strong>
            <span>We deliver, install, and collect the equipment at your event.</span>
            <small>Get the delivery and labor price in the next step</small>
          </span>
          <span className={styles.optionState}>{fulfillment === 'delivery' ? 'Selected' : 'Select'}</span>
        </button>
      </div>

      <div className={styles.reassurance}>
        <ol aria-label='Online booking steps'>
          <li className={styles.active} aria-current='step'><FiCalendar aria-hidden='true' /><span><b>1</b> Select time &amp; place</span></li>
          <li><span><b>2</b> Get a final price</span></li>
          <li><span><b>3</b> Book online</span></li>
        </ol>
        <div className={styles.mobileProgress} aria-label='Step 1 of 3, Select time and place'>
          <FiCalendar aria-hidden='true' />
          <strong>Step 1 of 3</strong>
          <span aria-hidden='true'>·</span>
          <span>Select time &amp; place</span>
        </div>
      </div>

      {fulfillment && <div className={styles.form}>
        <h3>{fulfillment === 'pickup' ? 'Choose pickup and return times' : 'Choose delivery and collection times'}</h3>
        {loadingProduct
          ? <div className={styles.loading} role='status'>Checking live availability and warehouse inventory…</div>
          : loadError
            ? <div className={styles.loadError} role='alert'>{loadError}</div>
            : fulfillment === 'pickup'
              ? <QuickRental data={rentalData || orderItem} appendToExistingItems={appendToExistingItems} embedded />
              : <ProductionPopup
                  initialItems={deliveryItems || [orderItem]}
                  appendInitialItems={appendToExistingItems}
                  builder={builder}
                  numId={builderIndex}
                  embedded
                />}
      </div>}
    </UiBoundary>
  )
}

export default RentalStartPopup
